// Postcode lookup helpers backed by postcodes.io. Shared by the engineer
// postcode-entry screen (JobSetup) and the Change Location drawer so both
// use exactly the same autocomplete and coordinate resolution.

const POSTCODES_API_BASE = 'https://api.postcodes.io/postcodes'

// Bound every external postcodes.io call so a slow / stalled request fails fast
// (AbortSignal timeout → caught below) instead of hanging the caller — e.g. the
// saved-location restore loader in JobSetup, which waits on lookupPostcode.
const REQUEST_TIMEOUT_MS = 8000

// Radius (metres) for resolving a device position to the nearest postcode.
// postcodes.io defaults to 100m, which is tighter than a typical phone fix —
// an engineer standing in the right place gets no result. The API caps the
// radius at 2000m, so we ask for the maximum and let the caller decide.
const NEAREST_POSTCODE_RADIUS_METRES = 2000

export interface PostcodeCoordinates {
  latitude: number
  longitude: number
}

export interface ResolvedPostcode extends PostcodeCoordinates {
  /**
   * The canonical Royal Mail spelling returned by postcodes.io — always
   * uppercase with the single space between the outward and inward codes
   * ("sw100ad" resolves to "SW10 0AD"). Persist this rather than what the
   * user typed: suppliers reject an unspaced postcode (OCTO-550159).
   */
  postcode: string
}

/**
 * Normalise a UK postcode for equality comparison: uppercase and strip all
 * whitespace, so "WS12 0AA", "ws120aa" and "WS12  0AA" compare equal. Two
 * genuinely different postcodes can never normalise to the same value.
 */
export function normalizePostcode(postcode: string): string {
  return postcode.toUpperCase().replace(/\s+/g, '')
}

/**
 * Autocomplete a partial UK postcode via postcodes.io.
 *
 * Returns an empty list for queries shorter than two characters or on any
 * network / API error — callers render "no suggestions" in those cases.
 */
export async function searchPostcodes(query: string): Promise<string[]> {
  if (!query || query.length < 2) return []

  try {
    const response = await fetch(
      `${POSTCODES_API_BASE}/${encodeURIComponent(query)}/autocomplete`,
      { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) },
    )
    if (!response.ok) return []
    const data = (await response.json()) as { result?: string[] | null }
    return data.result ?? []
  } catch {
    return []
  }
}

/**
 * Outcome of checking a postcode against postcodes.io. `not_found` means the
 * service answered and the postcode does not exist (or is terminated);
 * `failed` means we never got a usable answer (offline, timeout, outage), so
 * the postcode may still be real.
 */
export type PostcodeCheckOutcome =
  | { kind: 'found'; resolved: ResolvedPostcode }
  | { kind: 'not_found' }
  | { kind: 'failed' }

/**
 * Why a postcode the engineer entered could not be set.
 *
 * - `not_found`: the service answered and the postcode does not exist.
 * - `offline`: the device reports no connection, so it is certainly theirs.
 * - `unavailable`: no usable answer for any other reason — the service is
 *   down or erroring, the request timed out, or a network blocked it. These
 *   can't be told apart reliably, so the message blames neither side.
 */
export type PostcodeLookupFailure = 'not_found' | 'offline' | 'unavailable'

export const POSTCODE_FAILURE_MESSAGES: Record<PostcodeLookupFailure, string> = {
  not_found: "We couldn't find that postcode. Check it and try again.",
  offline: "You're offline. Check your internet connection and try again.",
  // Worded about the lookup, not the postcode: "couldn't check that postcode"
  // read too much like `not_found` and pointed the engineer at their input.
  unavailable: "Postcode lookup isn't working right now. Please try again in a moment.",
}

/**
 * Classify a check that got no usable answer. Only blames the connection when
 * the device itself says it is offline — a weak or blocked connection reports
 * as online, so it falls under the neutral `unavailable`.
 */
export function describeFailedPostcodeCheck(): Exclude<
  PostcodeLookupFailure,
  'not_found'
> {
  return typeof navigator !== 'undefined' && navigator.onLine === false
    ? 'offline'
    : 'unavailable'
}

/**
 * Check a full UK postcode via postcodes.io, telling "does not exist" apart
 * from "could not ask". Never throws.
 */
export async function checkPostcode(postcode: string): Promise<PostcodeCheckOutcome> {
  try {
    const response = await fetch(
      `${POSTCODES_API_BASE}/${encodeURIComponent(postcode)}`,
      { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) },
    )
    if (response.status === 404) return { kind: 'not_found' }
    if (!response.ok) return { kind: 'failed' }
    const data = (await response.json()) as {
      result?: { postcode?: string; latitude: number; longitude: number } | null
    }
    if (!data.result) return { kind: 'failed' }
    return {
      kind: 'found',
      resolved: {
        // Fall back to the caller's input on the (unexpected) chance the API
        // omits the postcode, so a successful lookup never resolves to "".
        postcode: data.result.postcode ?? postcode.trim().toUpperCase(),
        latitude: data.result.latitude,
        longitude: data.result.longitude,
      },
    }
  } catch {
    return { kind: 'failed' }
  }
}

/**
 * Resolve a full UK postcode to its canonical spelling and coordinates via
 * postcodes.io.
 *
 * The lookup accepts unspaced input ("SW100AD") and answers with the canonical
 * form ("SW10 0AD"), so callers get a value that is safe to store and show
 * without validating the format themselves.
 *
 * Returns null if the postcode is invalid or the lookup fails, so callers can
 * surface an "invalid postcode" message without throwing.
 */
export async function lookupPostcode(
  postcode: string,
): Promise<ResolvedPostcode | null> {
  const outcome = await checkPostcode(postcode)
  return outcome.kind === 'found' ? outcome.resolved : null
}

/**
 * Resolve a device position to the nearest UK postcode via postcodes.io.
 *
 * Backs "Use my current location": the device reports coordinates, and the
 * engineer needs a postcode to check before confirming. Returns null when no
 * postcode sits within NEAREST_POSTCODE_RADIUS_METRES (e.g. offshore, or
 * outside the UK) or the lookup fails, so callers can tell the engineer to
 * type one instead.
 */
export async function findNearestPostcode(
  latitude: number,
  longitude: number,
): Promise<string | null> {
  const query = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
    radius: String(NEAREST_POSTCODE_RADIUS_METRES),
    limit: '1',
  })

  try {
    const response = await fetch(`${POSTCODES_API_BASE}?${query.toString()}`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
    if (!response.ok) return null
    const data = (await response.json()) as {
      result?: Array<{ postcode?: string }> | null
    }
    return data.result?.[0]?.postcode ?? null
  } catch {
    return null
  }
}
